import { useState, useEffect } from 'react'
import { useGetReportReasonsQuery, useUpdateReportReasonsMutation } from '../../store/api/moderationApi'
import { Button, Alert, Modal, AgentFormField } from '../../components/ui'
import { COLOR_SUCCESS, COLOR_ERROR, ADMIN_SAVED_BANNER_TIMEOUT_MS, MESSAGES } from '../../lib/config'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

export function AdminModeration() {
  const { data, isLoading, isFetching, refetch } = useGetReportReasonsQuery()
  const [updateReasons, { isLoading: saving }] = useUpdateReportReasonsMutation()

  const [reasons, setReasons] = useState<string[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editIdx, setEditIdx] = useState<number | null>(null)
  const [formValue, setFormValue] = useState('')
  const [formError, setFormError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => { if (data?.reasons) setReasons(data.reasons) }, [data?.reasons])

  const isDirty = JSON.stringify(reasons) !== JSON.stringify(data?.reasons ?? [])
  const openCreate = () => { setEditIdx(null); setFormValue(''); setFormError(''); setShowForm(true) }
  const openEdit   = (i: number) => { setEditIdx(i); setFormValue(reasons[i]!); setFormError(''); setShowForm(true) }
  const closeForm  = () => { setShowForm(false); setEditIdx(null); setFormValue(''); setFormError('') }

  const handleFormSave = () => {
    const trimmed = formValue.trim()
    if (!trimmed) { setFormError('Reason cannot be empty'); return }
    if (trimmed.length > 100) { setFormError('Maximum 100 characters'); return }
    if (editIdx === null) {
      if (reasons.includes(trimmed)) { setFormError('This reason already exists'); return }
      if (reasons.length >= 20) { setFormError('Maximum 20 reasons allowed'); return }
      setReasons([...reasons, trimmed])
    } else {
      const others = reasons.filter((_, i) => i !== editIdx)
      if (others.includes(trimmed)) { setFormError('This reason already exists'); return }
      const next = [...reasons]; next[editIdx] = trimmed; setReasons(next)
    }
    closeForm()
  }

  const handleRemove = (idx: number) => {
    if (reasons.length <= 1) return
    if (!window.confirm(`Remove reason "${reasons[idx]}"?`)) return
    setReasons(reasons.filter((_, i) => i !== idx))
  }

  const handleMoveUp   = (idx: number) => { if (idx === 0) return; const next = [...reasons]; [next[idx - 1], next[idx]] = [next[idx]!, next[idx - 1]!]; setReasons(next) }
  const handleMoveDown = (idx: number) => { if (idx === reasons.length - 1) return; const next = [...reasons]; [next[idx], next[idx + 1]] = [next[idx + 1]!, next[idx]!]; setReasons(next) }

  const handleSave = async () => {
    setSaveError('')
    try {
      await updateReasons({ reasons }).unwrap()
      setSaved(true); setTimeout(() => setSaved(false), ADMIN_SAVED_BANNER_TIMEOUT_MS)
    } catch { setSaveError(MESSAGES.SAVE_FAILED) }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Report Reasons {!isLoading && `(${reasons.length})`}</div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isDirty && !saving && <span className={s.unsavedLabel}>Unsaved changes</span>}
          {saved && <span className={s.savedLabel} style={{ color: COLOR_SUCCESS }}>✓ Saved</span>}
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
          <Button size="sm" onClick={() => void handleSave()} disabled={!isDirty} loading={saving}>Save changes</Button>
          <Button size="sm" onClick={openCreate}>+ New Reason</Button>
        </div>
      </div>

      {saveError && <Alert variant="error" style={{ marginBottom: '1rem' }}>{saveError}</Alert>}

      {showForm && (
        <Modal onClose={closeForm} maxWidth="420px">
          <h3 className={s.formModalTitle}>{editIdx === null ? 'New Reason' : 'Edit Reason'}</h3>
          {formError && <Alert variant="error" style={{ marginBottom: '1rem' }}>{formError}</Alert>}
          <AgentFormField label="Reason text *">
            <input value={formValue} onChange={(e) => { setFormValue(e.target.value); setFormError('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleFormSave() }}
              placeholder="e.g. Spam or misleading" maxLength={100} autoFocus className={s.formInput} />
          </AgentFormField>
          <div className={s.charCount}>{formValue.length}/100 characters</div>
          <div className={s.formActions}>
            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleFormSave}>{editIdx === null ? 'Add reason' : 'Save changes'}</Button>
          </div>
        </Modal>
      )}

      <div className={s.listCard}>
        {isLoading && <div className={s.listCardLoading}>Loading reasons...</div>}
        {reasons.map((r, i) => (
          <div key={i} className={s.reasonRow}>
            <div className={s.moveButtons}>
              <button onClick={() => handleMoveUp(i)} disabled={i === 0} className={s.moveBtn}>▲</button>
              <button onClick={() => handleMoveDown(i)} disabled={i === reasons.length - 1} className={s.moveBtn}>▼</button>
            </div>
            <div className={s.reasonNumber} style={{ color: COLOR_ERROR }}>{i + 1}</div>
            <div className={s.reasonText}>{r}</div>
            <Button size="sm" variant="secondary" onClick={() => openEdit(i)}>✏️ Edit</Button>
            {reasons.length > 1 && <Button size="sm" variant="secondary" onClick={() => handleRemove(i)} style={{ color: 'var(--status-error)', borderColor: 'var(--status-error)' }}>Remove</Button>}
          </div>
        ))}
        {!isLoading && reasons.length === 0 && <div className={s.listCardEmpty}>No reasons found</div>}
      </div>
    </div>
  )
}
